/* eslint-disable qwik/no-use-visible-task */
import { cn } from "@/utils/tailwind-helper";
import type { QRL } from "@builder.io/qwik";
import { $, component$, useComputed$, useSignal, useStyles$, useVisibleTask$ } from "@builder.io/qwik";
import RichTextRenderer from "@caisy/rich-text-qwik-renderer";
import { FancyImage } from "../../../common-components/fancy-image/FancyImage";
import { useCurrentLocale } from "../../../hooks/useCurrentLocale";
import { CloseIcon } from "../../../icons/CloseIcon";
import { PageSearchIcon } from "../../../icons/PageSearchIcon";
import type { IGenNavigation_ExampleSearches, IGenPage } from "../../../services/graphql/__generated/sdk";
import { getInspectProps } from "../../../utils/getInspectProps";
import { PageSearchResultItem } from "../PageSearchResultItem";
import { PageSearchResultItemSkeleton } from "./PageSearchResultItemSkeleton";
import { useUniversalText } from "~/hooks/useCMSData";
import { scheduleAnimationCallback } from "@/utils/scheduleAnimationCallback";

const isSearchInputValid = (searchInput: string) => {
    return searchInput.length >= 1;
};

interface NavigationSearchRightProps {
    togglePageSearchRight$: QRL<() => void>;
    exampleSearches: IGenNavigation_ExampleSearches[];
    willToggle?: boolean;
    transitionDuration?: number;
}

type TSearchState = "examples" | "skeletons" | "results" | "no-results" | "idle";

export const NavigationSearchRight = component$<NavigationSearchRightProps>(
    ({ togglePageSearchRight$, exampleSearches, willToggle, transitionDuration: TRANSITION_DURATION = 500 }) => {
        const searchInput = useSignal<string>("");
        const lastSearchValue = useSignal<string>("");
        const searchResult = useSignal<{ hits?: IGenPage[] }>({});
        const currentLocale = useCurrentLocale();
        const isSearchInProgress = useSignal<boolean>(false);
        const showSkeletons = useSignal<boolean>(false);
        const { resultsNotFound = "No results found" } = useUniversalText();
        const fadeState = useSignal<"in" | "out" | "idle">("idle");
        const SKELETON_DELAY = 100;
        const FADE_DURATION = 150;
        const displayedSearchState = useSignal<TSearchState>("examples");
        const hasScrollbar = useSignal(false);
        const subMenuRef = useSignal<HTMLElement>(null!);

        useVisibleTask$(({ track }) => {
            track(() => displayedSearchState.value);
            if (!subMenuRef.value) return;

            hasScrollbar.value = subMenuRef.value.scrollHeight > window.innerHeight;
        });

        useVisibleTask$(({ track }) => {
            track(() => searchInput.value);
            searchResult.value = { hits: [] };
        });

        const handleSearchRequest = $(() => {
            if (searchInput.value === lastSearchValue.value || isSearchInputValid(searchInput.value) === false) {
                return;
            }

            displayedSearchState.value = "idle";
            fadeState.value = "idle";
            isSearchInProgress.value = true;
            showSkeletons.value = false;
            lastSearchValue.value = searchInput.value;

            // Start a timer to show skeletons if search takes longer than SKELETON_DELAY
            const skeletonTimer = setTimeout(() => {
                if (isSearchInProgress.value) {
                    showSkeletons.value = true;
                }
            }, SKELETON_DELAY);

            (async () => {
                const r = await fetch(
                    `/v1/page-search?locale=${encodeURIComponent(currentLocale)}&q=${encodeURIComponent(searchInput.value)}`,
                );
                if (r.status == 200) {
                    const resultObject = await r.json();
                    if (resultObject.q == searchInput.value) {
                        searchResult.value = resultObject;
                        // console.log(`searchResult.value ult.q`, resultObject);
                        // console.log(`searchResult.value`, searchResult.value);
                    }
                }

                isSearchInProgress.value = false;
                showSkeletons.value = false;
                clearTimeout(skeletonTimer);
                // console.log(`searchInput`, searchInput.value);
            })();
        });

        // Debounce Logic
        useVisibleTask$(({ track, cleanup }) => {
            track(() => searchInput.value);
            track(() => isSearchInProgress.value);

            const timer = setTimeout(handleSearchRequest, 500);

            cleanup(() => {
                clearTimeout(timer);
            });
        });

        const searchState = useComputed$<TSearchState>(() => {
            if (!isSearchInputValid(searchInput.value)) {
                return "examples";
            }
            if (isSearchInProgress.value && showSkeletons.value) {
                return "skeletons";
            }
            if (isSearchInProgress.value && !showSkeletons.value) {
                return "idle";
            }
            if (!isSearchInProgress.value && (searchResult.value?.hits?.length ?? 0) > 0) {
                return "results";
            }
            if (
                !isSearchInProgress.value &&
                lastSearchValue.value === searchInput.value &&
                searchResult.value?.hits?.length === 0
            ) {
                return "no-results";
            }
            return displayedSearchState.value === "examples" ? "examples" : "idle";
        });

        useVisibleTask$(({ track }) => {
            track(searchState);

            if (searchState.value !== displayedSearchState.value) {
                fadeState.value = "out";
                scheduleAnimationCallback(() => {
                    displayedSearchState.value = searchState.value;
                    fadeState.value = "in";
                }, FADE_DURATION);
            }
        });

        useStyles$(`
            @keyframes motion-bg-out {
                from {
                    background-color: rgb(34 51 59 / 0.3);
                    backdrop-filter: blur(30px);
                }
                to {
                    background-color: rgb(255 255 255 / 0);
                    backdrop-filter: blur(0px);
                }
            }

            @keyframes motion-bg-in {
                from {
                    background-color: rgb(255 255 255 / 0);
                    backdrop-filter: blur(0px);
                }
                to {
                    background-color: rgb(34 51 59 / 0.3);
                    backdrop-filter: blur(30px);
                }
            }
        `);

        return (
            <div
                class={cn(
                    "grid h-full grid-cols-4 gap-x-[16px] silver:grid-cols-8 silver:gap-x-[24px] gold:grid-cols-12 gold:gap-x-[24px] platinum:ml-auto platinum:max-w-[1440px]",
                    "relative overflow-hidden",

                    // this to compensate the shift because of the scroll bar
                    // "mr-[-15px]",
                )}
            >
                <div
                    class={cn("fixed inset-0")}
                    style={{
                        animationName: willToggle ? "motion-bg-out" : "motion-bg-in",
                        animationDuration: willToggle ? `${TRANSITION_DURATION * 1.5}ms` : `${TRANSITION_DURATION}ms`,
                        animationFillMode: "forwards",
                    }}
                />

                <div
                    class={cn(
                        "pointer-events-auto relative",
                        "bronze:col-span-4 silver:col-span-7 silver:col-start-2 gold:col-span-10 gold:col-start-3",
                        "motion-translate-x-in-100 motion-ease-in-quad",
                        willToggle && "motion-translate-x-out-100",
                        "overflow-y-auto",
                        "[&::-webkit-scrollbar]:w-2",
                        "[&::-webkit-scrollbar-thumb]:bg-primary-500",
                        "[&::-webkit-scrollbar-track]:bg-[#E9EBEB]",
                        hasScrollbar.value ? "silver:pr-0" : "silver:pr-2",
                    )}
                    style={{
                        "--motion-duration": `${TRANSITION_DURATION * 0.75}ms`,
                        "--motion-delay": willToggle ? `${TRANSITION_DURATION * 0.5}ms` : undefined,
                        background:
                            "linear-gradient(0deg, var(--primary-opacity-3, rgba(34, 51, 59, 0.03)) 0%, var(--primary-opacity-3, rgba(34, 51, 59, 0.03)) 100%), rgba(255, 255, 255, 0.99)",
                    }}
                >
                    <div class={cn("relative z-40 h-full px-4 silver:px-12 gold:px-20")}>
                        {/* close icon */}
                        <div
                            class={cn(
                                "relative",
                                "flex w-full justify-end",
                                "relative rounded-full silver:absolute silver:left-3 silver:top-3 silver:h-8 silver:w-8",
                            )}
                        >
                            <div
                                onClick$={togglePageSearchRight$}
                                class={cn(
                                    "z-40 pt-6 silver:left-3 silver:top-3 silver:p-0",
                                    "motion-opacity-in-0 motion-ease-in-quad",
                                    willToggle && "motion-opacity-out-0",
                                )}
                                style={{
                                    "--motion-duration": `${TRANSITION_DURATION}ms`,
                                }}
                            >
                                <div
                                    class={cn(
                                        "h-8 w-8 cursor-pointer rounded-full bg-primary-500/5 p-2 transition-colors duration-300 hover:bg-primary-500/20",
                                    )}
                                >
                                    <CloseIcon class={cn("h-4 w-4 text-primary-500")} />
                                </div>
                            </div>
                        </div>
                        <div
                            class={cn(
                                "bronze:col-span-4 silver:col-span-7 gold:col-span-10",
                                "border-b border-primary-500 border-opacity-10",
                                "flex items-center justify-between pb-4 pt-7",
                                "motion-opacity-in-0 motion-ease-in-quad silver:pt-20",
                                willToggle && "motion-opacity-out-0",
                            )}
                            style={{
                                "--motion-duration": `${TRANSITION_DURATION * 0.75}ms`,
                            }}
                        >
                            <div class={cn("flex w-full items-center gap-x-[10px]")}>
                                <input
                                    type="text"
                                    placeholder={"Start searching"}
                                    class={cn(
                                        "w-full bg-transparent outline-none", // Added bg-transparent and outline-none
                                        "text-[24px] font-normal leading-[32px] text-primary-500",
                                        "silver:text-[36px] silver:leading-[40px]",
                                        "gold:text-[48px] gold:leading-[48px]",
                                        "placeholder:text-primary-500/50", // Added placeholder styling
                                    )}
                                    bind:value={searchInput}
                                    onKeydown$={(event) => {
                                        if ((event as any).key === "Enter") {
                                            handleSearchRequest();
                                        }
                                    }}
                                />
                            </div>
                            <div class={cn("flex items-center gap-x-[10px]")} onClick$={handleSearchRequest}>
                                <PageSearchIcon
                                    class={cn(
                                        "h-8 w-8",
                                        "silver:h-10 silver:w-10",
                                        "gold:h-12 gold:w-12",
                                        isSearchInputValid(searchInput.value)
                                            ? "text-primary-500"
                                            : "text-primary-500/50",
                                    )}
                                />
                            </div>
                        </div>
                        <div
                            ref={subMenuRef}
                            class={cn(
                                "bronze:col-span-4 silver:col-span-8 gold:col-span-12",
                                "flex flex-col gap-y-[8px] gold:gap-y-[6px]",
                                "pb-4 pt-7 motion-ease-in-quad silver:pb-20 silver:pt-6",
                                fadeState.value === "in" && "motion-opacity-in-0",
                                fadeState.value === "out" && "motion-opacity-out-0",
                                willToggle && "motion-opacity-out-0",
                            )}
                            style={{
                                "--motion-duration": `${FADE_DURATION}ms`,
                            }}
                        >
                            <div
                                class={cn(
                                    "grid-cols-1 gap-y-[8px]",
                                    "silver:grid-cols-3 silver:gap-6",
                                    displayedSearchState.value === "examples" ? "grid" : "hidden",
                                )}
                            >
                                {exampleSearches.map((item) => (
                                    <div
                                        key={item.id}
                                        onClick$={$(() => {
                                            searchInput.value = `${item.searchValue}`;
                                            handleSearchRequest();
                                        })}
                                        class={cn(
                                            "flex cursor-pointer items-center justify-start gap-2 rounded bg-white/80 p-3 transition-colors duration-300",
                                            "bg-white/50",
                                        )}
                                    >
                                        {item.examplePage?.ogImage?.src && (
                                            <FancyImage
                                                img={item.examplePage?.ogImage}
                                                class={cn("h-[42px] w-[42px] rounded gold:w-[75px]")}
                                                responsiveAspectRatio={{
                                                    bronze: 1,
                                                    gold: 75 / 42,
                                                }}
                                                responsiveMaxWidth={{ bronze: 200 }}
                                                lazyload={false}
                                            />
                                        )}
                                        <div class={cn("flex items-start justify-center")}>
                                            <span
                                                {...getInspectProps({ id: item.id, fieldName: "exampleText" })}
                                                class={cn(
                                                    "text-sm font-medium leading-5 text-primary-500",
                                                    "gold:text-base",
                                                )}
                                            >
                                                {item.exampleText?.json && (
                                                    <RichTextRenderer node={item.exampleText.json} />
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {Array.from({ length: 5 }).map((_, idx) => (
                                <div
                                    key={idx}
                                    class={cn(displayedSearchState.value === "skeletons" ? "block" : "hidden")}
                                >
                                    <PageSearchResultItemSkeleton />
                                </div>
                            ))}

                            {searchResult.value?.hits?.map((item: IGenPage) => (
                                <div
                                    key={item.id}
                                    class={cn(displayedSearchState.value === "results" ? "block" : "hidden")}
                                >
                                    <PageSearchResultItem item={item} togglePageSearchRight$={togglePageSearchRight$} />
                                </div>
                            ))}

                            <div
                                class={cn(
                                    "py-4 text-center text-primary-500/70",
                                    displayedSearchState.value === "no-results" ? "block" : "hidden",
                                )}
                            >
                                {resultsNotFound}&nbsp;
                                <span class={cn("font-medium")}>"{searchInput.value}"</span>.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);
